import { useEffect, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiPlus } from 'react-icons/fi';
import styled from 'styled-components';
import { FeatureModel } from '../../interfaces/feature';
import { ThemeButton } from '../../theme/components/Button';
import { ThemeCard } from '../../theme/components/Card';
import ThemeContainer from '../../theme/components/Container';
import { ThemeHeadline } from '../../theme/components/Headline';
import { theme } from '../../theme/theme';
import { Feature } from './Feature';

export interface StepContainerItem2Props {
  onNextClicked: (features: FeatureModel[]) => void;
  onPreviousClicked: () => void;
}

export const StepContainerItem2: React.FC<StepContainerItem2Props> = ({ onNextClicked, onPreviousClicked }) => {
  const [features, setFeatures] = useState<FeatureModel[]>([]);

  const addEmptyFeature = () => {
    setFeatures([...features, { name: '', description: '' }]);
  };

  const updateFeature = (index: number, feature: FeatureModel) => {
    const newFeatures = [...features];
    newFeatures[index] = feature;
    setFeatures(newFeatures);
  };

  const handleNextClicked = () => {
    onNextClicked(features);
  };

  const handlePreviousClicked = () => {
    onPreviousClicked();
  };

  useEffect(() => {
    if (features.length === 0) {
      addEmptyFeature();
    }
  }, []);

  const hasAtLeastOneFeature = features.some(feature => feature.name.length > 0 && feature.description.length > 0);

  return (
    <Container>
      <ThemeHeadline variant='h3' title='Kano-Modell Feature-Priorisierung' />
      <ThemeHeadline title='Die Priorisierung von Features ist ein aufwändiges und kompliziertes unterfangen. Mit dem Kano-Modell gelingt es, die unterschiedlichen Feature-Ideen verschiedenen Kategorien zuzuordnen und daraus Prioritäten abzuleiten. Nutze unsere kostenlose Feature-Priorisierung nach dem Kano-Modell.' />

      <Card>
        <ThemeHeadline variant='h3' title='Schritt 2: Feature Ideen' />
        <ThemeHeadline title='Welche Feature-Ideen möchtest Du priorisieren lassen?' />
        <FeatureContainer>
          {features.map((feature, index) => (
            <Feature key={index} feature={feature} onChange={newFeature => updateFeature(index, newFeature)} />
          ))}
        </FeatureContainer>
        <AddFeatureButton variant='secondary' onClick={addEmptyFeature} iconLeft={<FiPlus />}>
          Feature hinzufügen
        </AddFeatureButton>
        <Spacer />
        <ButtonGroup>
          <ThemeButton onClick={handlePreviousClicked} variant='secondary' iconLeft={<FiChevronLeft />}>
            Zurück
          </ThemeButton>
          <ThemeButton onClick={handleNextClicked} iconRight={<FiChevronRight />} disabled={!hasAtLeastOneFeature}>
            KI-Analyse durchführen
          </ThemeButton>
        </ButtonGroup>
      </Card>
    </Container>
  );
};

const Container = styled(ThemeContainer)`
  gap: ${theme.spacing.l};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
  margin: 120px auto;

  @media screen and (max-width: ${theme.responsive.breakpoints.medium}) {
    margin: ${theme.spacing.xl} auto;
    padding: 0 ${theme.spacing.d};
    box-sizing: border-box;
  }
`;

const Card = styled(ThemeCard)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.d};
  padding: ${theme.spacing.xxl} ${theme.spacing.xl};
  width: 100%;
  flex: 1;

  @media screen and (max-width: ${theme.responsive.breakpoints.medium}) {
    padding: ${theme.spacing.xl} ${theme.spacing.d};
    padding-bottom: ${theme.spacing.d};
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  justify-content: space-between;
  width: 100%;
  margin-top: 64px;

  @media screen and (max-width: ${theme.responsive.breakpoints.medium}) {
    flex-direction: column;
    gap: ${theme.spacing.d};
    justify-content: center;
    align-items: stretch;
  }
`;

const FeatureContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.d};
  flex-direction: column;
  width: 100%;
  margin-top: ${theme.spacing.xxl};
  box-sizing: border-box;

  @media screen and (max-width: ${theme.responsive.breakpoints.medium}) {
    gap: ${theme.spacing.l};
  }
`;

const AddFeatureButton = styled(ThemeButton)`
  align-self: flex-end;
`;
